"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import type { Map as MLMap } from "maplibre-gl";
import {
  ButtonLink,
  ChevronDown,
  Download,
  Icon,
  IconButton,
  Maximize,
  Minus,
  Plus,
  RotateCcw,
  Search,
  TriangleAlert,
  cx,
} from "@/components";
import { BrandLockup } from "@/components/brand-lockup";
import { getMapTokens, type MapSpec, type PointSet, type TileDescriptor } from "@/lib/map";
import { linkifyUgc } from "@/lib/text/linkifyUgc";
import { pointSetFromSnapshot, type MapSnapshot } from "@/lib/share";
import { resolveOverlayData } from "@/lib/overlay/resolve";
import type { OverlayEngine } from "@/lib/overlay/engine";

// Lazy: pdf-lib + the export dialog are a separate chunk, loaded only when an
// owner has enabled viewer export — the default embed bundle stays lean (AC-7).
const ExportDialog = dynamic(() => import("../../(editor)/share/ExportDialog").then((m) => m.ExportDialog), {
  ssr: false,
});
// Deep import of the browser-safe engine — the "@/lib/nl" barrel re-exports
// server-only (pg) modules that must never enter a viewer bundle.
import { savedViewMatchIds, savedViewSpec } from "@/lib/nl/savedView";
import type { SavedView } from "@/lib/contracts/nl";
import type { EmbedOptions } from "@/lib/embed/params";
import { MapCanvas, type MapView, type OverlayRenderData } from "../../(editor)/map/MapCanvas";
import { LAYER_POINTS } from "../../(editor)/map/mapLayers";
import { Locator } from "./Locator";
import "./viewer.css";

export interface ViewerSurfaceProps {
  snapshot: MapSnapshot;
  tile: TileDescriptor;
  /** Esri satellite descriptor (MAP-102). */
  satelliteTile?: TileDescriptor;
  themeName: string;
  /** White-label brand name (server-resolved via getTheme) — never a literal. */
  brandName: string;
  /**
   * White-label wordmark (text + optional logo asset, server-resolved via
   * getTheme) — the brand logo shown in the free-plan growth banner's lockup.
   */
  wordmark: { text: string; logoUrl: string | null };
  /** Free-plan growth banner (brand lockup + "Create your own map" CTA) — free
   *  tier keeps it on (the embed-led growth loop). */
  showBranding: boolean;
  /** viewer = full /m/{slug} page chrome (title plate); embed = minimal frame. */
  variant: "viewer" | "embed";
  /** The published slug — powers the ?ref= backlink and the report/event beacons. */
  slug: string;
  /** Owner-authored description (FAIL-453) — rendered on the viewer titleplate when present. */
  description?: string | null;
  /** Cached OG thumbnail — the no-WebGL `<noscript>` fallback image (FR-11). */
  ogImageUrl?: string | null;
  /** Owner-enabled viewer export (AC-6, default off): gates the export affordance. */
  canExport?: boolean;
  options: EmbedOptions;
  /**
   * M9 deploy-time overlay engine (provider seam). When bound + the published
   * spec carries an `overlay` block, the embed redraws the SAME choropleth the
   * editor did (resolved via the shared `resolveOverlayData`). Absent → the
   * overlay layer stays inert (the wedge default).
   */
  overlayEngine?: OverlayEngine;
}

/** The published quick-filter column: the color-by column, else a small categorical. */
function filterableColumn(pointSet: PointSet, spec: MapSpec): string | null {
  const colorCol = spec.encoding.colorBy?.column;
  if (colorCol) {
    const col = pointSet.columns.find((c) => c.name === colorCol);
    if (col && col.distinctSample.length >= 2) return col.name;
  }
  const fallback = pointSet.columns.find(
    (c) => c.type === "text" && c.distinctSample.length >= 2 && c.distinctSample.length <= 8,
  );
  return fallback?.name ?? null;
}

const FILTER_VALUE_CAP = 12;

/**
 * ViewerSurface — the shared read-only render shell for /m/{slug} and
 * /embed/{slug}. Reconstructs the PointSet from the published snapshot
 * (zero geocoding) and mounts the SAME MapCanvas the editor uses in
 * mode="embed", with read-only chrome around it: title plate (viewer),
 * zoom/fullscreen column, one quick-filter chip, the non-removable
 * attribution (inside MapCanvas), and the free-plan growth banner.
 */
export function ViewerSurface({
  snapshot,
  tile,
  satelliteTile,
  themeName,
  brandName,
  wordmark,
  showBranding,
  variant,
  slug,
  description,
  ogImageUrl,
  canExport = false,
  options,
  overlayEngine,
}: ViewerSurfaceProps) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);

  const pointSet = useMemo(() => pointSetFromSnapshot(snapshot), [snapshot]);
  /** Promoted saved views published with this map (AC-10) — replayed with zero LLM. */
  const savedViews = useMemo<SavedView[]>(() => snapshot.saved_views ?? [], [snapshot]);

  /** Published spec + the pinned view (or fit-to-data when none was pinned). */
  const publishedSpec = useMemo<MapSpec>(() => {
    const spec = snapshot.spec;
    const view = snapshot.pinned_view
      ? { center: snapshot.pinned_view.center, zoom: snapshot.pinned_view.zoom, fit: false }
      : { ...spec.view, fit: true };
    return { ...spec, view };
  }, [snapshot]);

  const filterCol = useMemo(
    () => (options.filter ? filterableColumn(pointSet, publishedSpec) : null),
    [options.filter, pointSet, publishedSpec],
  );
  const filterValues = useMemo(() => {
    if (!filterCol) return [];
    const seen = new Set<string>();
    for (const point of pointSet.points) {
      const value = (point.props[filterCol] ?? "").trim();
      if (value !== "") seen.add(value);
      if (seen.size > FILTER_VALUE_CAP) break;
    }
    return [...seen].slice(0, FILTER_VALUE_CAP).sort((a, b) => a.localeCompare(b));
  }, [pointSet, filterCol]);

  const [chipValue, setChipValue] = useState<string | null>(null);
  const [chipOpen, setChipOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIds, setHighlightIds] = useState<string[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [view, setView] = useState<MapView | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [reportState, setReportState] = useState<"idle" | "sending" | "done">("idle");

  /** Fire-and-forget attribution backlink beacon (AC-9) — survives navigation. */
  function recordAttributionClick() {
    try {
      const body = JSON.stringify({ slug, type: "attribution_click", surface: variant });
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        navigator.sendBeacon("/api/s/event", new Blob([body], { type: "application/json" }));
      } else {
        void fetch("/api/s/event", { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true });
      }
    } catch {
      // best-effort — a dropped beacon must never block the backlink navigation
    }
  }

  /** Fire-and-forget marker-interaction beacon (FAIL-467) — one ping per pin click. */
  function recordMarkerInteraction() {
    try {
      const body = JSON.stringify({ slug, surface: variant });
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        navigator.sendBeacon("/api/s/marker", new Blob([body], { type: "application/json" }));
      } else {
        void fetch("/api/s/marker", { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true });
      }
    } catch {
      // best-effort — a dropped beacon must never affect the map interaction
    }
  }

  /** Discreet abuse report (minimal intake): one tap lodges a report against this slug. */
  async function submitReport() {
    if (reportState !== "idle") return;
    setReportState("sending");
    try {
      await fetch("/api/report", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ slug, reason: "other" }),
      });
      setReportState("done");
    } catch {
      setReportState("idle");
    }
  }

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement != null);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const activeView = useMemo(
    () => savedViews.find((v) => v.id === activeViewId) ?? null,
    [savedViews, activeViewId],
  );

  const renderedSpec = useMemo<MapSpec>(() => {
    // A promoted saved view folds its style overlay on first (AC-10, zero LLM).
    let spec = activeView ? savedViewSpec(publishedSpec, activeView) : publishedSpec;
    if (options.legend !== null) {
      spec = { ...spec, legend: { ...spec.legend, enabled: options.legend } };
    }
    if (filterCol && chipValue !== null) {
      spec = { ...spec, filters: [...spec.filters, { column: filterCol, op: "is", value: chipValue }] };
    }
    return spec;
  }, [publishedSpec, options.legend, filterCol, chipValue, activeView]);

  // M9 — resolve the published `overlay` block into the SAME choropleth the
  // editor rendered (deterministic redraw), via the shared resolver + engine
  // seam. No engine bound (the wedge) → null → inert overlay layer.
  const [overlayData, setOverlayData] = useState<OverlayRenderData | null>(null);
  const overlayKey = renderedSpec.overlay ? JSON.stringify(renderedSpec.overlay) : "";
  useEffect(() => {
    let cancelled = false;
    const block = renderedSpec.overlay;
    const resolve =
      overlayEngine && block
        ? resolveOverlayData(overlayEngine, block, getMapTokens(themeName))
        : Promise.resolve(null);
    resolve
      .then((data) => {
        if (!cancelled) setOverlayData(data);
      })
      .catch(() => {
        if (!cancelled) setOverlayData(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayEngine, overlayKey, themeName]);

  /** Replay a saved view deterministically — recompute matches + fly to framing. No model. */
  function selectView(view: SavedView | null) {
    setActiveViewId(view?.id ?? null);
    if (!view) {
      setHighlightIds([]);
      return;
    }
    setHighlightIds(savedViewMatchIds(view, pointSet.points));
    const map = mapRef.current;
    if (view.viewport && map) {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        map.jumpTo({ center: view.viewport.center, zoom: view.viewport.zoom });
      } else {
        map.easeTo({ center: view.viewport.center, zoom: view.viewport.zoom });
      }
    }
  }

  function handleReady(map: MLMap) {
    mapRef.current = map;
    if (options.panLock) {
      map.dragPan.disable();
      map.scrollZoom.disable();
      map.boxZoom.disable();
      map.doubleClickZoom.disable();
      map.touchZoomRotate.disable();
      map.keyboard.disable();
    }
    // Marker-interaction beacon (FAIL-467): count pin clicks on the viewer/embed
    // surfaces only (this handler is viewer/embed-scoped; the editor never mounts it).
    map.on("click", LAYER_POINTS, () => recordMarkerInteraction());
  }

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void frameRef.current?.requestFullscreen?.();
    }
  }

  /** Find-a-place: fly to the first point whose title/props match the query. */
  function submitSearch() {
    const needle = query.trim().toLowerCase();
    if (needle === "") {
      setHighlightIds([]);
      return;
    }
    const titleCol = publishedSpec.popup.titleColumn ?? pointSet.columns[0]?.name;
    const match =
      pointSet.points.find((p) => titleCol && (p.props[titleCol] ?? "").toLowerCase().includes(needle)) ??
      pointSet.points.find((p) => Object.values(p.props).some((v) => v.toLowerCase().includes(needle)));
    if (!match) return;
    setHighlightIds([match.id]);
    const map = mapRef.current;
    if (map) {
      map.easeTo({ center: [match.lng, match.lat], zoom: Math.max(map.getZoom(), 12) });
    }
  }

  /** Recenter to the published view — the framing the map loaded with — so a user
   *  who accidentally panned/zoomed away can always get back. Mirrors MapCanvas's
   *  initial framing: a pinned view eases to its center/zoom; a fit-to-data map
   *  re-fits the point bounds (same padding/maxZoom as MapCanvas.fitToBounds). */
  function recenter() {
    const map = mapRef.current;
    if (!map) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const v = publishedSpec.view;
    if (v.fit) {
      const b = pointSet.bounds;
      if (!b) return;
      map.fitBounds(
        [
          [b.minLng, b.minLat],
          [b.maxLng, b.maxLat],
        ],
        { padding: 64, maxZoom: 14, animate: !reduce },
      );
    } else if (reduce) {
      map.jumpTo({ center: v.center, zoom: v.zoom });
    } else {
      map.easeTo({ center: v.center, zoom: v.zoom });
    }
  }

  const locator = options.mode === "store_locator";
  const showZoom = options.zoom && !options.panLock;
  // Recenter is useful whenever the map can be panned/zoomed away from its view.
  const showRecenter = !options.panLock;

  // Extracted so it can render in two places: inside the viewer's top-left
  // cluster (stacked below the title plate) or, on an embed with no plate, as a
  // standalone top-left control.
  const filterChip =
    !locator && filterCol && filterValues.length > 0 ? (
      <div className={cx("mv-chiprail", variant === "embed" && "mv-chiprail--embed")}>
        <button
          type="button"
          className="mv-chip"
          aria-expanded={chipOpen}
          onClick={() => setChipOpen((open) => !open)}
        >
          {filterCol}
          <span className="mv-chip__value">{chipValue ?? "All"}</span>
          <Icon icon={ChevronDown} size="sm" />
        </button>
        {chipOpen && (
          <div className="mv-chipmenu" role="listbox" aria-label={`Filter by ${filterCol}`}>
            <button
              type="button"
              role="option"
              aria-selected={chipValue === null}
              className="mv-chipmenu__item"
              onClick={() => {
                setChipValue(null);
                setChipOpen(false);
              }}
            >
              All
            </button>
            {filterValues.map((value) => (
              <button
                key={value}
                type="button"
                role="option"
                aria-selected={chipValue === value}
                className="mv-chipmenu__item"
                onClick={() => {
                  setChipValue(value);
                  setChipOpen(false);
                }}
              >
                {value}
              </button>
            ))}
          </div>
        )}
      </div>
    ) : null;

  // The embed-led growth backlink target — home, tagged with ?ref={slug} so the
  // click is attributable to the referring map (AC-9).
  const growthHref = `/?ref=${encodeURIComponent(slug)}`;

  const mapRegion = (
    <div className={cx("mv-map", showBranding && "mv-map--branded")}>
      <MapCanvas
        pointSet={pointSet}
        spec={renderedSpec}
        mode="embed"
        tile={tile}
        satelliteTile={satelliteTile}
        themeName={themeName}
        highlightIds={highlightIds}
        overlayData={overlayData}
        fallbackImageUrl={ogImageUrl}
        onReady={handleReady}
        onViewChange={locator ? setView : undefined}
      />

      {variant === "viewer" ? (
        <div className="mv-topleft">
          <div className="mv-titleplate">
            <h1 className="mv-titleplate__title">{snapshot.title !== "" ? snapshot.title : `${brandName} map`}</h1>
            <p className="mv-titleplate__meta">
              <span className="mv-titleplate__count">
                {snapshot.point_count.toLocaleString()} {snapshot.point_count === 1 ? "place" : "places"}
              </span>
            </p>
            {description && (
              // Owner-authored (FAIL-453) — plain text; any URL the owner typed
              // renders as a real link but carries rel="nofollow ugc" so it can't
              // pass SEO authority through unreviewed user content.
              <p className="mv-titleplate__description">{linkifyUgc(description, "mv-titleplate__description-link")}</p>
            )}
          </div>
          {/* Chip stacks BELOW the plate (flex column) so it can never overlap the
              growth link — the bug the fixed-offset absolute chip caused. */}
          {filterChip}
        </div>
      ) : (
        filterChip
      )}

      {!locator && options.search && (
        <form
          className={cx("mv-search", variant === "embed" && !filterCol && "mv-search--top")}
          onSubmit={(e) => {
            e.preventDefault();
            submitSearch();
          }}
          role="search"
        >
          <Icon icon={Search} size="sm" className="mv-search__glyph" />
          <input
            className="mv-search__input"
            type="search"
            placeholder="Find a place…"
            aria-label="Find a place"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </form>
      )}

      {!locator && savedViews.length > 0 && (
        <div className="mv-views" role="group" aria-label="Saved views">
          <button
            type="button"
            className={cx("mv-chip", activeViewId === null && "mv-chip--active")}
            aria-pressed={activeViewId === null}
            onClick={() => selectView(null)}
          >
            All
          </button>
          {savedViews.map((v) => (
            <button
              key={v.id}
              type="button"
              className={cx("mv-chip", activeViewId === v.id && "mv-chip--active")}
              aria-pressed={activeViewId === v.id}
              onClick={() => selectView(v)}
            >
              {v.sourceQuestion}
            </button>
          ))}
        </div>
      )}

      {(showZoom || showRecenter || options.fullscreen || canExport) && (
        <div className="mv-controls">
          {showZoom && (
            <>
              <IconButton label="Zoom in" tooltip="left" onClick={() => mapRef.current?.zoomIn()}>
                <Icon icon={Plus} size="md" />
              </IconButton>
              <IconButton label="Zoom out" tooltip="left" onClick={() => mapRef.current?.zoomOut()}>
                <Icon icon={Minus} size="md" />
              </IconButton>
            </>
          )}
          {showRecenter && (
            <IconButton label="Reset view" tooltip="left" onClick={recenter}>
              <Icon icon={RotateCcw} size="md" />
            </IconButton>
          )}
          {options.fullscreen && (
            <IconButton label={fullscreen ? "Exit fullscreen" : "Fullscreen"} tooltip="left" onClick={toggleFullscreen}>
              <Icon icon={Maximize} size="md" />
            </IconButton>
          )}
          {canExport && (
            <IconButton label="Export this map" tooltip="left" onClick={() => setExportOpen(true)}>
              <Icon icon={Download} size="md" />
            </IconButton>
          )}
        </div>
      )}

      {canExport && exportOpen && (
        <ExportDialog
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          pointSet={pointSet}
          spec={renderedSpec}
          title={snapshot.title !== "" ? snapshot.title : `${brandName} map`}
          attribution={snapshot.attribution}
          getMap={() => mapRef.current}
          onError={() => setExportOpen(false)}
        />
      )}

      {/* Free-plan growth banner — a rounded pill floating over the map (the same
          translucent, blurred, hairline treatment as the marketing top nav):
          brand lockup on the left, "Create your own map" CTA on the right. Both
          are real <a href> carrying ?ref={slug} (the AC-9 growth backlink); on
          embeds they open Mapize in a new tab. */}
      {showBranding && (
        <div className="mv-banner">
          <BrandLockup
            brandName={brandName}
            wordmark={wordmark}
            href={growthHref}
            target={variant === "embed" ? "_blank" : undefined}
            rel={variant === "embed" ? "noreferrer" : undefined}
            onClick={recordAttributionClick}
          />
          {/* Full-size (default md) CTA — CTAs are always full-size, never the
              compact sm. The pill hugs the taller button. */}
          <ButtonLink
            href={growthHref}
            variant="upgrade"
            target={variant === "embed" ? "_blank" : undefined}
            rel={variant === "embed" ? "noreferrer" : undefined}
            onClick={recordAttributionClick}
          >
            Create Your Own Map
          </ButtonLink>
        </div>
      )}

      <button
        type="button"
        className="mv-report"
        disabled={reportState !== "idle"}
        aria-label="Report this map"
        onClick={() => void submitReport()}
      >
        <Icon icon={TriangleAlert} size="sm" />
        {reportState === "done" ? "Reported" : reportState === "sending" ? "Reporting…" : "Report"}
      </button>

      {/* No-JS / no-WebGL fallback (FR-11): the cached thumbnail + a link to the full viewer. */}
      <noscript>
        <div className="mv-noscript">
          {ogImageUrl ? (
            // next/image needs JS/hydration; a no-JS fallback must use a plain <img>.
            // eslint-disable-next-line @next/next/no-img-element
            <img
              className="mv-noscript__img"
              src={ogImageUrl}
              alt={snapshot.title !== "" ? snapshot.title : `${brandName} map`}
            />
          ) : null}
          <a className="mv-noscript__link" href={`/map/${slug}/`}>
            View interactive map
          </a>
        </div>
      </noscript>
    </div>
  );

  if (!locator) {
    return (
      <div ref={frameRef} className="mv-surface">
        {mapRegion}
      </div>
    );
  }

  return (
    <div ref={frameRef} className="mv-surface mv-surface--locator">
      <Locator
        pointSet={pointSet}
        spec={publishedSpec}
        view={view}
        onSelect={(point) => {
          setHighlightIds([point.id]);
          const map = mapRef.current;
          if (map) map.easeTo({ center: [point.lng, point.lat], zoom: Math.max(map.getZoom(), 13) });
        }}
        onAnchor={(center) => {
          const map = mapRef.current;
          if (map) map.easeTo({ center, zoom: Math.max(map.getZoom(), 11) });
        }}
        selectedId={highlightIds[0] ?? null}
      >
        {mapRegion}
      </Locator>
    </div>
  );
}
